import React from 'react'
import classNames from 'classnames'
import { Image } from 'react-bootstrap'
import { Tooltip } from 'antd'

import { useSelector } from 'react-redux'
import logo from 'assets/defaultCourse.png'
import { ICourseCard } from 'models/courseCard.model'
import Img from 'components/ui/Img'
import { useTags } from 'components/ui/CourseTags/useTags'
import { CourseType, EnrolState } from 'enum'
import { IMainState } from 'store/states'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import { useWindowSize } from 'hooks/react.hooks'
import { mobileWidth } from 'enum/responsive-width.enum'
import {
  useCourseStatus,
  useAmplitudeActions,
  useStartCourse,
} from '../../../hooks'
import {
  CourseCardBase,
  ProgressBarStateCourse,
  RequiresRequest,
} from '../../atoms'
import { ICardEnrol } from '../../../types'
import { ExternalCourseIndicator } from '../externalCourseIndicator'
import styles from './CourseHorizontalCard.module.scss'

interface ICourseHorizontalCardsProps {
  course: ICourseCard
  directToLessons?: boolean
  enrolled: boolean
  className?: string
  enrol: ICardEnrol | null
  maxWidth?: number | string | null
  showProgress?: boolean
  noAvailable?: boolean
}

const MAX_TAGS = 2
export const CourseHorizontalCard: React.FC<ICourseHorizontalCardsProps> = ({
  course,
  directToLessons,
  className,
  enrol,
  maxWidth = 440,
  showProgress = false,
  noAvailable,
}) => {
  const courseStatus = useCourseStatus()

  const enrolled = !!enrol?.id

  const windowsSize = useWindowSize()
  const onDesktop =
    windowsSize.width && windowsSize.width < mobileWidth ? false : true
  const amplitudeActions = useAmplitudeActions({
    course,
    enrolled,
    enrol,
  })
  const { permission } = useSelector(({ user }: IMainState) => user)

  const startCourse = useStartCourse({
    course,
    directToLessons,
    enrol,
  })

  const handleButton = () => {
    if (enrol && enrol?.imported && !enrol?.manuallyAssigned) return
    startCourse()
    amplitudeActions()
  }
  const requiresRequest = course.courseType === CourseType.Closed

  const isApproved = enrol?.stateAdmin === EnrolState.Approved
  const isPending = enrol?.stateAdmin === EnrolState.Pending
  const showState = !!enrolled && (requiresRequest ? isApproved : true)

  const percent =
    enrol && enrol.progressPercentage ? enrol.progressPercentage : 0

  const tags = useTags({ course, enrol })

  const isAdmin = permission.admin || permission.superadmin

  return (
    <CourseCardBase
      id={`${course.id}`}
      className={classNames(styles.card, className)}
      onClick={() => handleButton()}
      style={{ maxWidth: maxWidth ? maxWidth : undefined }}
      noAvailable={noAvailable}
    >
      <div className={styles.image}>
        <Img
          component={Image}
          imageHandlerProps={{
            height: 200,
          }}
          src={course.thumbnailUrl || course.bannerUrl || logo}
          onError={(e) => {
            e.currentTarget.src = logo
          }}
        />
      </div>
      <ExternalCourseIndicator
        origin="HORIZONTAL_CARD"
        url={course['integrationCourseLink']}
      />
      <div className={styles.content}>
        {!!tags.length && (
          <div className={styles.tags}>
            {tags.slice(0, MAX_TAGS).map((tag, index) => (
              <div
                className={styles.tag}
                key={index}
                onClick={(e) => e.stopPropagation()}
              >
                {tag}
              </div>
            ))}
            {tags.length > MAX_TAGS && (
              <div onClick={(e) => e.stopPropagation()}>
                <Tooltip
                  placement="top"
                  overlayClassName={styles.tooltip}
                  title={
                    <div className={styles.tags}>
                      {tags.slice(MAX_TAGS).map((tag, index) => (
                        <div className={styles.tag} key={index}>
                          {tag}
                        </div>
                      ))}
                    </div>
                  }
                >
                  <div className={classNames(styles.tag, styles.tagCount)}>{`+${
                    tags.length - MAX_TAGS
                  }`}</div>
                </Tooltip>
              </div>
            )}
          </div>
        )}
        {course.name.length >= 51 && onDesktop ? (
          <CommonToolTip text={course.name} position="bottom">
            <div className={styles.title}>{course.name}</div>
          </CommonToolTip>
        ) : (
          <div className={styles.title}>{course.name}</div>
        )}
        <div className="flex-fill" />
        {showState || showProgress ? (
          <div>
            <ProgressBarStateCourse
              advance={enrol?.advance || 0}
              progress={percent}
            />
          </div>
        ) : (
          !isAdmin &&
          requiresRequest && <RequiresRequest isPending={isPending} />
        )}
        {(!!showState || showProgress) && (
          <div className={styles.advance}>
            {courseStatus(enrol?.advance || 0)}
            <div className="flex-fill text-right">{percent}%</div>
          </div>
        )}
      </div>
    </CourseCardBase>
  )
}

export default CourseHorizontalCard
